import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface BusinessCategoryDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
}

export interface BusinessCategoryState {
    businessCategories: BusinessCategoryDto[];
    businessCategory: BusinessCategoryDto;
    gettingBusinessCategories: boolean;
    savingBusinessCategory: boolean;
    gettingBusinessCategory: boolean;
    archivingBusinessCategory: boolean;
    showDeleteBusinessCategoryModal: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    savingStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const blankBusinessCategoryDto: BusinessCategoryDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
};

const initialState: BusinessCategoryState = {
    businessCategories: [],
    businessCategory: blankBusinessCategoryDto,
    gettingBusinessCategories: false,
    savingBusinessCategory: false,
    gettingBusinessCategory: false,
    archivingBusinessCategory: false,
    showDeleteBusinessCategoryModal: false,
    status: 'idle',
    savingStatus: 'idle',
    error: undefined,
};

export const businessCategorySlice = createSlice({
    name: 'businessCategories',
    initialState,
    reducers: {
        clearBusinessCategories: (state) => {
            state.businessCategories = [];
            state.businessCategory = blankBusinessCategoryDto;
        },
        updateBusinessCategory: (state, param) => {
            const { payload } = param;
            state.businessCategory = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getBusinessCategories.pending, (state, action) => {
                state.gettingBusinessCategories = true;
                state.status = 'loading';
            })
            .addCase(getBusinessCategories.fulfilled, (state, action) => {
                state.gettingBusinessCategories = false;
                state.businessCategories = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getBusinessCategories.rejected, (state, action) => {
                state.gettingBusinessCategories = false;
                state.businessCategories = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(saveBusinessCategory.pending, (state, action) => {
                state.savingBusinessCategory = true;
                state.savingStatus = 'loading';
            })
            .addCase(saveBusinessCategory.fulfilled, (state, action) => {
                state.savingBusinessCategory = false;
                state.savingStatus = 'succeeded';
            })
            .addCase(saveBusinessCategory.rejected, (state, action) => {
                state.savingBusinessCategory = false;
                state.savingStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(getBusinessCategory.pending, (state, action) => {
                state.gettingBusinessCategory = true;
                state.status = 'loading';
            })
            .addCase(getBusinessCategory.fulfilled, (state, action) => {
                state.gettingBusinessCategory = false;
                state.businessCategory = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getBusinessCategory.rejected, (state, action) => {
                state.gettingBusinessCategory = false;
                state.businessCategory = blankBusinessCategoryDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveBusinessCategory.pending, (state, action) => {
                state.archivingBusinessCategory = true;
                state.status = 'loading';
            })
            .addCase(archiveBusinessCategory.fulfilled, (state, action) => {
                state.archivingBusinessCategory = false;
                state.businessCategories = state.businessCategories.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveBusinessCategory.rejected, (state, action) => {
                state.archivingBusinessCategory = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearBusinessCategories } = businessCategorySlice.actions;
export const { updateBusinessCategory } = businessCategorySlice.actions;

export const getBusinessCategories = createAsyncThunk('business-categories/getBusinessCategories', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/business-categories`);

    return response.data;
});

export const getBusinessCategory = createAsyncThunk('business-categories/getBusinessCategory', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/business-categories/${id}`);

    return response.data;
});

export const archiveBusinessCategory = createAsyncThunk('business-categories/archiveBusinessCategory', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/business-categories/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedBusinessCategory'),
        })
    );
    return response.data;
});

export const saveBusinessCategory = createAsyncThunk(
    'business-categories/saveBusinessCategory',
    async (businessCategoryToSave: BusinessCategoryDto, thunkAPI) => {
        if (businessCategoryToSave.id === 0) {
            const response = await axios.post(`/api/synergy/business-categories/create`, businessCategoryToSave);
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('createBusinessCategory'),
                })
            );
            return response.data;
        } else {
            const response = await axios.put(`/api/synergy/business-categories/put`, businessCategoryToSave);
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('saveBusinessCategory'),
                })
            );
            return response.data;
        }
    }
);
